import { Download } from '@carbon/icons-react'
import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react'
import { LoadingAnimation } from 'components'
import _ from 'lodash'
import { useMemo } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatDate } from '../formatDate'
import { colorScale, parseDate, useDraggableChart } from '../helpers'
import { useLegend } from './useLegend'
import { FiltersT } from '..'
import { useOnDownloadImg } from '../MainSection/useOnDownload'

export type SeriesData = {
  id: string
  data: {
    x: string
    y: number
    numOfTests: number
    source: number
  }[]
}[]

type PropTypes = {
  data: {
    date: string
    value: number
    diseaseName: string
    numOfTests: number
    source: number
    name: string
  }[]
  title?: string
  yAxisTitle: string
  dateRange: FiltersT['dateRange']
  changeDateRange: (start: string, end: string) => void
  onDownload: () => void
  dataStatus: { isLoading: boolean; isError: boolean; isFetched: boolean }
  decimalPlaces?: number
  tooltipFormat?: (val: string) => string
}

export const GenericLineChart = ({
  data,
  title,
  yAxisTitle,
  dateRange,
  changeDateRange,
  onDownload,
  dataStatus,
  decimalPlaces = 2,
  tooltipFormat = (x) => x,
}: PropTypes) => {
  const diseaseGrouped = useMemo(() => _.groupBy(data, 'diseaseName'), [data])

  const diseases = useMemo(() => Object.keys(diseaseGrouped), [diseaseGrouped])
  const diseaseSorter = useMemo(
    () =>
      Object.fromEntries(
        diseases.sort((a, b) => a.localeCompare(b)).map((x, i) => [x, i])
      ),
    [diseases]
  )
  const xValues = useMemo(
    () =>
      Array.from(new Set(data.map((x) => x.date))).sort(
        (a, b) => parseDate(a).getTime() - parseDate(b).getTime()
      ),
    [data]
  )

  // Convert to series data format
  const mappedData = useMemo(
    () =>
      Object.entries(diseaseGrouped).map(([diseaseName, row]) => ({
        id: diseaseName,
        data: row.map((d) => {
          return {
            x: d.date,
            y: d.value,
            numOfTests: d.numOfTests,
            source: d.source,
          }
        }),
      })) as SeriesData,
    [diseaseGrouped]
  )

  // Transform into the format recharts expects
  const transformed = useMemo(
    () =>
      xValues.map((x) => {
        const obj: any = { x }
        mappedData.forEach((s) => {
          const point = s.data.find((d) => d.x === x)
          obj.numOfTests = point?.numOfTests ?? 0
          obj.source = point?.source ?? 0
          obj[s.id] = point ? point.y : undefined
        })
        return obj
      }),
    [mappedData, xValues]
  )

  const isLoading = dataStatus.isLoading
  const isError = dataStatus.isError
  const isFetched = dataStatus.isFetched
  const noData = transformed.length === 0 && isFetched

  const {
    refAreaLeft,
    refAreaRight,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useDraggableChart({ changeDateRange })

  const {
    handleLegendMouseEnter,
    handleLegendMouseLeave,
    selectLegend,
    selectedLegends,
  } = useLegend()

  const { ref, onDownloadImage } = useOnDownloadImg(
    `${(title ?? 'image').replace(/ /, '_')}_${dateRange.start}_${dateRange.end}.png`
  )

  return (
    <Box
      ref={ref}
      border='1px solid'
      borderColor='gray3'
      flex={1}
      h='100%'
      borderRadius={'10px'}
      position={'relative'}
      p={4}
      pt={20}
      pb={20}
    >
      <Box
        position='absolute'
        top={4}
        left={4}
        right={4}
        display='flex'
        justifyContent='space-between'
      >
        <Text fontWeight='bold'>{title}</Text>
        <Menu>
          <MenuButton>
            <Button variant={'ghost'} w={'24px'} h={'24px'} p={0}>
              <Download size={16} />
            </Button>
          </MenuButton>
          <MenuList className='ignore-download'>
            <MenuItem onClick={onDownload}>CSV</MenuItem>
            <MenuItem onClick={onDownloadImage}>PNG</MenuItem>
          </MenuList>
        </Menu>
      </Box>
      {isLoading || noData ? (
        <Flex justifyContent={'center'} alignItems={'center'} h='100%' w='100%'>
          {isLoading ? (
            <LoadingAnimation height='100%' />
          ) : isError ? (
            'Something went wrong!'
          ) : (
            'No data available'
          )}
        </Flex>
      ) : (
        <ResponsiveContainer width='100%' height='100%'>
          <ComposedChart
            data={transformed}
            margin={{ left: 20 }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <XAxis
              type='number'
              dataKey={(d) => parseDate(d.x).getTime()}
              angle={-35}
              textAnchor='end'
              tick={{ fontSize: 12 }}
              tickFormatter={(x) => formatDate(new Date(x))}
              domain={[
                parseDate(dateRange.start).getTime(),
                parseDate(dateRange.end).getTime(),
              ]}
            >
              <Label value='Date' position='bottom' offset={50} />
            </XAxis>
            <YAxis
              yAxisId='left'
              interval='preserveStartEnd'
              scale='linear'
              tick={{ fontSize: 12 }}
            >
              <Label
                angle={270}
                position='left'
                style={{ textAnchor: 'middle' }}
                offset={-10}
              >
                {yAxisTitle}
              </Label>
            </YAxis>

            {/* Keep this so that the graph aligns with TestPositivity */}
            <YAxis
              yAxisId='right'
              orientation='right'
              interval='preserveStartEnd'
              scale='linear'
              display={undefined}
            />
            <CartesianGrid stroke='#eee' strokeDasharray='5 5' />
            {diseases.map((x) => (
              <Line
                key={x}
                yAxisId='left'
                type='monotone'
                dataKey={x}
                stroke={colorScale(x)}
                strokeWidth={selectedLegends.includes(x) ? 3 : 1}
                dot={false}
                connectNulls
              />
            ))}

            {refAreaLeft && refAreaRight && (
              <ReferenceArea
                yAxisId='left'
                x1={refAreaLeft}
                x2={refAreaRight}
                strokeOpacity={0.3}
                fill='yellow'
                fillOpacity={0.05}
              />
            )}
            <Tooltip
              labelFormatter={(x) => formatDate(new Date(x))}
              formatter={(value, name) => [
                name === 'Source'
                  ? value
                  : tooltipFormat(
                      parseFloat(value.toString()).toFixed(decimalPlaces)
                    ),
                name,
              ]}
              itemSorter={(item) => diseaseSorter[item.name as string]}
            />
            <Legend
              onClick={selectLegend}
              onMouseOver={handleLegendMouseEnter}
              onMouseOut={handleLegendMouseLeave}
              wrapperStyle={{
                position: 'relative',
                marginTop: 50,
                fontSize: 12,
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Box>
  )
}
